import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  FormControl,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  TextField,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { getTracks, getTrackDriverEloRankings, TrackEloRanking } from '../services/api';
import { MatchupsModal } from '../components/MatchupsModal';

export const Matchups: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tracks, setTracks] = useState<{ name: string }[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);
  const [rankings, setRankings] = useState<TrackEloRanking[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tracksData = await getTracks();
        setTracks(tracksData);
      } catch (err) {
        setError('Failed to load tracks');
        console.error('Error loading tracks:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchRankings = async () => {
      if (!selectedTrack) {
        setRankings([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const results = await getTrackDriverEloRankings({ track: selectedTrack });
        setRankings(results);
      } catch (err) {
        setError('Failed to fetch driver rankings');
        console.error('Error fetching rankings:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRankings();
  }, [selectedTrack]);

  const handleDriverClick = (driver: string) => {
    setSelectedDriver(driver);
    setIsModalOpen(true);
  };

  const formatEloDiff = (diff: number): string => {
    if (diff === 0) return '0';
    return diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: isMobile ? 1 : 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Driver Matchups
        </Typography>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          alignItems={isMobile ? "stretch" : "center"}
        >
          <FormControl sx={{ flex: isMobile ? 1 : 2 }}>
            <Autocomplete
              value={selectedTrack}
              onChange={(_, newValue) => setSelectedTrack(newValue)}
              options={tracks.map((track) => track.name)}
              sx={{
                '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Track"
                  placeholder="Search tracks..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : rankings.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Driver</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Power Ranking
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankings.map((ranking, index) => (
                <TableRow
                  key={ranking.Driver}
                  hover
                  onClick={() => handleDriverClick(ranking.Driver)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 'medium' }}>{index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 'medium' }}>{ranking.Driver}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 'medium',
                      color: ranking.elo_diff > 0 ? '#2E7D32' : ranking.elo_diff < 0 ? '#C62828' : 'text.primary',
                    }}
                  >
                    {formatEloDiff(ranking.elo_diff)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedTrack && selectedDriver && (
        <MatchupsModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          track={selectedTrack}
          driver={selectedDriver}
        />
      )}
    </Container>
  );
};
